import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  Package, 
  DollarSign, 
  TrendingUp, 
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  Download,
  Building,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Plus
} from 'lucide-react';
import Button from '../../../components/common/Button';
import Card from '../../../components/ui/Card';
import DataTable from '../../../components/ui/DataTable';
import StatCard from '../../../components/dashboard/StatCard';
import Chart from '../../../components/dashboard/Chart';
import { useOrders } from '../../../hooks/useOrders';
import { useQuotations } from '../../../hooks/useQuotations';

// Import the same banner images as SellerDashboard
import Banner1 from "../../../assets/styles/Girl.png";
import Banner2 from "../../../assets/styles/delighted-young-woman-clad-vibrant-red-dress-is-captured-pointing-her-right-with-look_116547-116725.jpg";
import Banner3 from "../../../assets/styles/girl-having-high-hopes-wishing-enter-university-cross-fingers-good-luck-looking-upper-left-co_1258-142110.jpg";

// Slider data with buyer-specific content - All buttons navigate to products page
const slides = [
  {
    id: 1,
    title: "Find Quality Products!",
    description: "Browse our verified supplier catalog with bulk pricing",
    image: Banner1,
    cta: "Browse Products",
    link: "/buyer/products"
  },
  {
    id: 2,
    title: "Get Custom Quotes",
    description: "Request personalized pricing for bulk orders",
    image: Banner2,
    cta: "Explore Products",
    link: "/buyer/products"
  },
  {
    id: 3,
    title: "Track Your Orders",
    description: "Monitor all your purchases in real-time",
    image: Banner3,
    cta: "View Products",
    link: "/buyer/products"
  }
];

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [timeRange, setTimeRange] = useState('month');
  const { orders, loading: ordersLoading, stats: orderStats } = useOrders();
  const { quotations, loading: quotesLoading, stats: quoteStats } = useQuotations();
  
  const recentOrders = orders.slice(0, 5);
  const pendingQuotes = quotations.filter(q => q.status === 'pending').slice(0, 5);

  // Auto-rotate slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleSliderAction = (link) => {
    navigate(link);
  };

  const stats = [
    {
      title: 'Total Orders',
      value: orderStats.total,
      change: '+12%',
      trend: 'up',
      icon: <ShoppingCart className="h-6 w-6" />,
      color: 'blue'
    },
    {
      title: 'Active Quotes',
      value: quoteStats.pending,
      change: '+5',
      trend: 'up',
      icon: <Package className="h-6 w-6" />,
      color: 'green'
    },
    {
      title: 'Total Spend',
      value: `$${orderStats.totalSpend.toLocaleString()}`,
      change: '+8%',
      trend: 'up',
      icon: <DollarSign className="h-6 w-6" />,
      color: 'purple'
    },
    {
      title: 'Avg. Order Value',
      value: `$${orderStats.avgOrderValue}`,
      change: '-2%',
      trend: 'down',
      icon: <TrendingUp className="h-6 w-6" />,
      color: 'orange'
    }
  ];

  const chartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Orders',
        data: [12, 19, 3, 5, 2, 3],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true
      },
      {
        label: 'Spend ($)',
        data: [12000, 19000, 3000, 5000, 2000, 3000],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true
      }
    ]
  };

  const orderColumns = [
    {
      id: 'orderId',
      header: 'Order ID',
      accessor: 'id',
      render: (item) => (
        <Link to={`/buyer/orders/${item.id}`} className="text-blue-600 hover:underline">
          #{item.id.slice(-8)}
        </Link>
      )
    },
    {
      id: 'date',
      header: 'Date',
      accessor: 'createdAt',
      render: (item) => new Date(item.createdAt).toLocaleDateString()
    },
    {
      id: 'supplier',
      header: 'Supplier',
      accessor: 'supplier.name'
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: 'total',
      render: (item) => `$${item.total.toLocaleString()}`
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      render: (item) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          item.status === 'delivered' ? 'bg-green-100 text-green-800' :
          item.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
          item.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {item.status}
        </span>
      )
    }
  ];

  const quoteColumns = [
    {
      id: 'quoteId',
      header: 'Quote ID',
      accessor: 'id',
      render: (item) => `#${item.id.slice(-8)}`
    },
    {
      id: 'product',
      header: 'Product',
      accessor: 'product.name'
    },
    {
      id: 'supplier',
      header: 'Supplier',
      accessor: 'supplier.name'
    },
    {
      id: 'quantity',
      header: 'Quantity',
      accessor: 'quantity'
    },
    {
      id: 'price',
      header: 'Price',
      accessor: 'price',
      render: (item) => `$${item.price.toLocaleString()}`
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      render: (item) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          item.status === 'accepted' ? 'bg-green-100 text-green-800' :
          item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
          item.status === 'rejected' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {item.status}
        </span>
      )
    }
  ];

  const quickActions = [
    {
      title: 'Browse Products',
      description: 'Find new suppliers and products',
      icon: <Package className="h-6 w-6" />,
      link: '/buyer/products',
      color: 'blue'
    },
    {
      title: 'Request Quote',
      description: 'Get custom pricing for bulk orders',
      icon: <DollarSign className="h-6 w-6" />,
      link: '/buyer/quotations/new',
      color: 'green'
    },
    {
      title: 'Track Orders',
      description: 'Monitor your recent purchases',
      icon: <ShoppingCart className="h-6 w-6" />,
      link: '/buyer/orders',
      color: 'purple'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          {/* <h1 className="text-2xl font-bold text-gray-900">Buyer Dashboard</h1> */}
          <p className="text-gray-600">Welcome back! Here's what's happening with your business.</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" size="small">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline" size="small">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Image Slider - Same as SellerDashboard */}
      <div className="relative rounded-2xl overflow-hidden shadow-lg">
        <div className="relative h-64 md:h-72">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${slide.image})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
              
              <div className="relative h-full flex items-center">
                <div className="container mx-auto px-6 md:px-12">
                  <div className="max-w-lg">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                      {slide.title}
                    </h2>
                    <p className="text-white/90 text-lg mb-6">
                      {slide.description}
                    </p>
                    <button
                      onClick={() => handleSliderAction(slide.link)}
                      className="inline-flex items-center bg-orange-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors"
                    >
                      {slide.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Slider Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors backdrop-blur-sm"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors backdrop-blur-sm"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        
        {/* Slide Indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide ? 'bg-white w-8' : 'bg-white/50 w-2'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-3">
          <Card title="Quick Actions">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {quickActions.map((action, index) => (
                <Link
                  key={index}
                  to={action.link}
                  className="flex items-center p-6 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors group hover:shadow-md"
                >
                  <div className={`p-3 rounded-xl mr-4 ${
                    action.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                    action.color === 'green' ? 'bg-green-100 text-green-600' :
                    'bg-purple-100 text-purple-600'
                  }`}>
                    {action.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 group-hover:text-orange-600">
                      {action.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">{action.description}</p>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-gray-400 group-hover:text-orange-600" />
                </Link>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Recent Orders & Quotes */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card 
          title="Recent Orders" 
          action={
            <Link to="/buyer/orders" className="text-orange-600 hover:text-orange-500 text-sm font-medium">
              View All
            </Link>
          }
        >
          <DataTable
            columns={orderColumns}
            data={recentOrders}
            loading={ordersLoading}
            pagination={false}
            onRowClick={(item) => navigate(`/buyer/orders/${item.id}`)}
          />
        </Card>

        <Card 
          title="Pending Quotes" 
          action={
            <Link to="/buyer/quotations" className="text-orange-600 hover:text-orange-500 text-sm font-medium">
              View All
            </Link>
          }
        >
          <DataTable
            columns={quoteColumns}
            data={pendingQuotes}
            loading={quotesLoading}
            pagination={false}
            onRowClick={(item) => navigate(`/buyer/quotations/${item.id}`)}
          />
        </Card>
      </div>

      {/* Supplier Recommendations */}
      <Card 
        title="Recommended Suppliers" 
        action={
          <Link to="/buyer/products" className="text-orange-600 hover:text-orange-500 text-sm font-medium">
            Browse All Suppliers
          </Link>
        }
      >
        <div className="grid md:grid-cols-3 gap-6">
          {[1, 2, 3].map((supplier) => (
            <div key={supplier} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                  <Building className="h-6 w-6 text-gray-500" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Supplier {supplier}</h3>
                  <p className="text-sm text-gray-500">Verified • 4.8★</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Specializes in industrial machinery with 15+ years experience
              </p>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">Min. Order: $1,000</span>
                <Button 
                  size="small" 
                  onClick={() => navigate('/buyer/products')}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  View Products
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default BuyerDashboard;